import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://wasabi-storage-backend.hostings.workers.dev',
  timeout: 15000, // Reduced timeout to 15 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth headers if needed
api.interceptors.request.use(
  (config) => {
    // Add any auth tokens here if implementing authentication
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.error('Unauthorized access');
    } else if (error.response?.status === 429) {
      // Handle rate limiting
      console.error('Rate limit exceeded');
    } else if (error.response?.status >= 500) {
      // Handle server errors
      console.error('Server error');
    }
    return Promise.reject(error);
  }
);

// Upload related API calls
export const uploadAPI = {
  /**
   * Request upload URL from backend
   */
  async requestUpload(fileData) {
    const response = await api.post('/api/upload/request', fileData);
    return response.data;
  },

  /**
   * Upload file directly to S3 using pre-signed URL
   */
  async uploadToS3(uploadUrl, file, onProgress) {
    const response = await axios.put(uploadUrl, file, {
      // Don't send Content-Type header to avoid signature mismatch with S3
      headers: {},
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response;
  },

  /**
   * Confirm upload completion
   */
  async confirmUpload(uploadData) {
    const response = await api.post('/api/upload/confirm', uploadData);
    return response.data;
  },

  /**
   * Get upload limits and restrictions
   */
  async getUploadLimits() {
    const response = await api.get('/api/upload/limits');
    return response.data;
  },
};

// Download related API calls
export const downloadAPI = {
  /**
   * Get file information by share ID with retry logic
   */
  async getFileInfo(shareId) {
    let lastError;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await api.get(`/api/download/info/${shareId}`);
        return response.data;
      } catch (error) {
        lastError = error;
        
        // Don't retry on 404 (file not found) or 410 (expired)
        if (error.response?.status === 404 || error.response?.status === 410) {
          throw error;
        }
        
        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
    }
    
    throw lastError;
  },

  /**
   * Request download URL with optional password
   */
  async requestDownload(shareId, password = null) {
    const response = await api.post('/api/download/request', {
      shareId,
      password,
    });
    return response.data;
  },

  /**
   * Download file directly with progress tracking
   */
  async downloadFile(shareId, password = null, onProgress = null) {
    try {
      // First get the download URL
      const downloadResponse = await this.requestDownload(shareId, password);
      const { downloadUrl, filename, mimeType, size } = downloadResponse.data;

      // Download the actual file with progress tracking
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
          }
        }
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

      return {
        success: true,
        filename,
        size,
        mimeType
      };

    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  },

  /**
   * Verify file password
   */
  async verifyPassword(shareId, password) {
    const response = await api.post('/api/download/verify-password', {
      shareId,
      password,
    });
    return response.data;
  },

  /**
   * Get download statistics
   */
  async getDownloadStats(shareId) {
    const response = await api.get(`/api/download/stats/${shareId}`);
    return response.data;
  },
};

// General API calls
export const generalAPI = {
  /**
   * Get system health status
   */
  async getHealth() {
    const response = await api.get('/api/files/health');
    return response.data;
  },

  /**
   * Get upload statistics
   */
  async getStats(timeframe = '24h') {
    const response = await api.get(`/api/files/stats?timeframe=${timeframe}`);
    return response.data;
  },

  /**
   * Warm up the backend worker (call on app load)
   */
  async warmUp() {
    try {
      await api.get('/api/files/health', { timeout: 5000 });
    } catch (error) {
      // Silently fail warmup requests
      console.log('Backend warmup failed:', error.message);
    }
  },
};

// Error handling utilities
export const handleAPIError = (error) => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  } else if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else {
    return 'An unexpected error occurred';
  }
};

export default api; 